<template>
  <app-layout>
    <Head title="Contacts"/>
    <template #header>
      <div class="md:flex justify-between items-center pb-1">
        <div class="mr-4 md:mr-5 flex justify-start items-center">
          <PageBackHeader href="/contacts" label="Contacts" :subtitle="contact.full_name" />
        </div>
      </div>
    </template>

    <div class="max-w-7xl mx-auto flex mb-6">
      <div class="w-full p-2 sm:px-6 lg:px-8">
        <div class="grid grid-cols-12 gap-6">
          <div class="col-span-12 lg:col-span-4">
            <div class="bg-white shadow-xl p-4 sm:rounded-lg sm:p-6">
              <div class="flex justify-between mb-5">
                <h2 class="font-semibold text-xl text-gray-800 leading-tight mb-2">Contact Details</h2>
                <Link :href="route('contacts.edit.form', contact.id)" class="text-blue-600">
                  <font-awesome-icon :icon="['far', 'pen-to-square']" class="text-xl mr-2 text-gray-500"></font-awesome-icon>
                </Link>
              </div>
              <div class="flex w-full mb-5 ">
                <div class="w-1/2">
                    <div class="text-gray-600 text-xs">First name</div>
                    <p>{{ contact.first_name }}</p>
                </div>
                <div class="w-1/2">
                    <div class="text-gray-600 text-xs">Last name</div>
                    <p>{{ contact.last_name }}</p>
                </div>
              </div>
              <div class="mb-5">
                <div class="text-gray-60 text-xs">Phone number</div>
                <p>{{ contact.phone_number }}</p>
              </div>

              <div class="mb-5">
                <div class="text-gray-600 text-xs">Lists</div>
                <span v-if="contact.groups.data.length">
                  <a v-for="list in lists" class="text-blue-600"
                    :href="'/groups/view/' + list.tra_id"
                    :key="list.id"
                  >
                    {{ list.title }} <br />
                  </a>
                </span>
                <p v-if="!lists.length">--</p>
              </div>

              <div class="mb-5">
                <div class="text-gray-600 text-xs">Assigned to</div>
                <p>{{ contact.assigned_to_user || '--' }}</p>
              </div>

              <!-- custom fields -->
              <contact-field-seperator v-if="$page.props.customFields"/>
              <custom-fields-display-for-contact 
                v-if="$page.props.customFields"
                :contact="contact" 
                :customFields="$page.props.custom_fields.data"
              />

              <!-- end custom fields-->

                <contact-field-seperator />

                <div 
                    @click="detailsExpanded = !detailsExpanded" 
                    class="flex justify-between items-center cursor-pointer mb-3"
                >
                    <h3 class="text-lg font-medium">Details</h3>
                    
                    <font-awesome-icon :icon="['fas', 'caret-up']" 
                        class="text-gray-600 text-xl"
                        :rotation="detailsExpanded ? null : 180"
                    />
                </div>

                <template v-if="detailsExpanded">
                    <div class="mb-5">
                        <div class="text-gray-600 text-xs">Last contacted</div>
                        <p><date-tooltip :data="lastContacted" /></p>
                    </div>
                    <div class="mb-5">
                        <div class="text-gray-600 text-xs">Subscribed via</div>
                        <p>{{ contact.created_method !== null ? contact.created_method : "--" }}</p>
                    </div>
                    <div class="mb-5">
                        <div class="text-gray-600 text-xs">Date added</div>
                        <p><date-tooltip :data="contact.created_at" /></p>
                    </div>
                </template>

                
                <contact-field-seperator />
              <div class="text-center">
                <div class="sm:inline-block">
                  <button class="text-gray-700 focus:outline-none" @click="toggleDeletionModal(contact.id)">
                    Delete Contact
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="col-span-12 lg:col-span-8">
            <div class="flex justify-between border-b-2 pb-3">
              <h2 class="font-semibold text-xl text-gray-800 leading-tight mb-2">History</h2>
              <button
                class="rounded-full leading-5 bg-blue-400 px-4 py-2 text-white"
                @click="sendMessage(contact.id)"
              >
                <span class="mr-1 leading-5">+</span>
                New Message to {{ contact.first_name }}
              </button>
            </div>
            <div class="history overflow-auto" style="max-height:560px;">
              <div v-for="message in prepareMessages" :key="message.id" class="bg-white shadow-sm p-2 sm:rounded-lg sm:p-6 lg:p-8 mt-5">
                <div class="msg-box">
                  <div class="flex justify-between ">
                    <p class="font-bold text-gray-900 mb-2 flex items-center">
                      <span :class="message.status" class="label h-4 w-4 block rounded-full mr-3"></span>
                      Message {{ message.status }}
                    </p>
                    <span class="text-gray-600" v-if="message.true_send_time != null">
                        <date-tooltip :data="message.true_send_time" 
                            dateFormat="MMMM D, YYYY h:mm A"
                            :showTooltip="false"
                        />
                    </span>
                  </div>
                  <div class="text-gray-800 flex items-start flex-wrap" v-if="message.status === 'delivered' && !message.group_id">
                    <p class="w-full">
                      <span class="italic text-gray-600">{{ contact.full_name }}  was sent a message <Link v-if="message.message_deliverable_tra_id" :href="route('message.details', message.message_deliverable_tra_id)" class="text-blue-600 text-sm ml-1">(view details)</Link>.</span>
                    </p>
                    <p class="w-full block mt-2">{{ message.parsed_message }}</p>
                  </div>
                  <div class="text-gray-800 flex items-start flex-wrap" v-else-if="message.status === 'error' && !message.group_id">
                    <p class="w-full">
                      <span class="italic text-gray-600">{{ contact.full_name }}  was sent a message that failed to send.</span>
                    </p>
                    <p class="w-full block mt-2">{{ message.parsed_message }}</p>
                  </div>
                  <div class="text-gray-800 flex items-start flex-wrap" v-else-if="message.status === 'delivered' && message.group_id">
                    <p class="w-full">
                      <span class="italic text-gray-600">
                        {{ contact.full_name }}  was sent a message from the
                        <a v-if="!groupDeleted(message.group_id)" :href="'/groups/view/' + findGroupTraId(message.group_id)" class="text-blue-600 p-2">
                          {{ findGroupName(message.group_id) }}
                        </a><span v-else class="p-2">{{ findGroupName(message.group_id) }}</span> list.
                      </span>
                    </p>
                    <p class="w-full block mt-2">{{ message.parsed_message }}</p>
                  </div>
                  <div class="text-gray-800 flex items-start flex-wrap" v-else-if="message.status === 'error' && message.group_id">
                    <p class="w-full">
                      <span class="italic text-gray-600">
                        {{ contact.full_name }}  was sent a message from the
                      <a v-if="!groupDeleted(message.group_id)" :href="'/groups/view/' + findGroupTraId(message.group_id)" class="text-blue-600 p-2">
                        {{ findGroupName(message.group_id) }}
                      </a><span v-else class="p-2">{{ findGroupName(message.group_id) }}</span> list that failed to send.
                      </span>
                    </p>
                    <p class="w-full block mt-2">{{ message.parsed_message }}</p>
                  </div>
                </div>
              </div>
              <div class="bg-white shadow-sm p-2 sm:rounded-lg sm:p-6 lg:p-8 mt-5">
                <div class="msg-box">
                  <div class="flex justify-between ">
                    <p class="font-bold text-gray-900 mb-2">Contact Added</p>
                    <span class="text-gray-600"><date-tooltip :data="contact.created_at" /></span>
                  </div>
                  <p v-if="contact.created_method" class="text-gray-800">{{ contact.full_name }} was added as a contact via {{ contact.created_method }}.</p>
                  <p v-else class="text-gray-800">{{ contact.full_name }} was added.</p>
                </div>
              </div>
              <!--      HIDDEN TEMPORARILY        -->
              <!--              <div v-if="contact.history.length" v-for="history in contact.history" :key="history.id" class="bg-white shadow-sm p-2 sm:rounded-lg sm:p-6 lg:p-8 mt-5">-->
              <!--                <div class="msg-box">-->
              <!--                  <div class="flex justify-between">-->
              <!--                    <p class="font-bold text-gray-900 mb-2">Contact Updated</p>-->
              <!--                    <span class="text-gray-600"><DateTooltip :data="history.created_at" /></span>-->
              <!--                  </div>-->
              <!--                  <p class="text-gray-800">-->
              <!--                    {{ getFormattedColumnName(history.key) }} was changed from {{ getFormatedValue(history.old_value, history.key) }} to {{ getFormatedValue(history.new_value, history.key) }}.-->
              <!--                  </p>-->
              <!--                </div>-->
              <!--              </div>-->

            </div>
          </div>
        </div>
      </div>

    </div>
    <jet-dialog-modal v-if="showDeletionModal" :show="showDeletionModal" @close="showDeletionModal = false">
      <template #title>
        Delete Contact
      </template>

      <template #content>
        Are you sure you want to delete this contact?
      </template>

      <template #footer>
        <jet-secondary-button @click="toggleDeletionModal">
          Cancel
        </jet-secondary-button>
        
        <jet-danger-button class="ml-2" @click.native="deleteContact(contactSetForDeletion)">
        Delete Contact
        </jet-danger-button>
        
      </template>
    </jet-dialog-modal>
  </app-layout>
</template>

<script>
import AppLayout from "../../Layouts/AppLayout";
import JetDropdown from "../../Jetstream/Dropdown";
import JetDropdownLink from "../../Jetstream/DropdownLink";
import JetDialogModal from "../../Jetstream/DialogModal";
import JetSecondaryButton from "../../Jetstream/SecondaryButton";
import JetNavLink from "../../Jetstream/NavLink";
import Button from "../../Jetstream/Button";
import JetDangerButton from "../../Jetstream/DangerButton";
import DateTooltip from '../../components/DateTooltip.vue'
import CustomFieldsDisplayForContact from "@/components/CustomFields/CustomFieldsDisplayForContact.vue";

import ContactFieldSeperator from '@/components/Contacts/ContactFieldSeperator.vue';
import axios from "axios";

export default {
  components: {
    AppLayout,
    JetDropdown,
    JetDropdownLink,
    JetDialogModal,
    JetNavLink,
    Button,
    JetSecondaryButton,
    JetDangerButton,
    DateTooltip,
    CustomFieldsDisplayForContact,
    ContactFieldSeperator,
  },
  props: {
    data: Array,
    links: Object,
    meta: Object,
    contact: Object
  },
  data() {
    return {
      showDeletionModal: false,
      detailsExpanded: true,
      showEditModal: false,
      contactEditDetails: {
        first_name: null,
        last_name: null,
        phone_number: null,
        id: null
      },
      contactSetForDeletion: null,
    };
  },
  mounted() {
    if ( this.contact.history ) {
      this.contact.history.reverse();
    }
  },
  computed: {
    contactDeleteRoute: function() {
      if (!this.contactSetForDeletion) {
        return "";
      }
      return route("contacts.delete", {
        id: this.contactSetForDeletion
      });
    },
    prepareMessages() {
      let messages = this.contact.messages;
      messages  = messages.filter((message) => {
        return (message.status == "delivered" || message.status == "error");
      });
      return messages.reverse();
    },
    lastContacted() {
      let message = this.prepareMessages.find(function (e) {
        if(e.true_send_time) {
          return e.true_send_time != null
        }
      });
      if (message) {
        return message.true_send_time
      } else {
        return null;
      }
    },
    lists () {
      return this.contact.groups.data.filter(group => !group.deleted_at)
    }
  },
  methods: {
    deleteContact(id) {
      let vm = this;
      axios
        .post(`/contacts/delete/${id}`, {
          redirectToContacts: true
        })
        .then(function(res) {
          vm.$inertia.visit("/contacts")

          vm.$toasted.show(res.data, {
            position: "bottom-right",
            type: "success",
            duration: 3000
          });
        })
        .catch(function(err) {
          vm.$inertia.visit("/contacts");
          vm.$toasted.show(err.response.data.message, {
            position: "bottom-right",
            type: "error",
            duration: 3000
          });
        });
      document.body.style.overflow = null
    },
    sendMessage(contactId) {
      let vm = this;
      vm.$inertia.visit(route("messages.create", { contact_id: contactId }));
    },
    findGroupName(id) {
      const contactGroup =  this.contact.groups.data.find(group => group.id === id ) || null;
      if (contactGroup){
        return contactGroup.deleted_at ? contactGroup.title +" (Deleted List)" : contactGroup.title
      }
      else if (contactGroup === null) {
        let group = this.prepareMessages.find(message => message.group_id === id )
        if (group.recipient_name.deleted_at) {
          return group.recipient_name.title + ' (Deleted),(Removed)'
        } else {
          return group.recipient_name.title + ' (Removed)'
        }
      }
      else {
        return '--'
      }
    },
    getFieldValue(traId) {
      return this.contact.custom_fields[traId] || null;
    },
    groupDeleted(id){
      const contactGroup =  this.contact.groups.data.find(group => group.id === id ) || null;
      if (contactGroup){
        return contactGroup.deleted_at ? true : false
      } else if (contactGroup === null) {
        let group = this.prepareMessages.find(message => message.group_id === id )
        if(group.recipient_name.deleted_at){
          return true;
        } else {
          return false
        }
      } else {
        return false
      }
    },
    findGroupTraId(id) {
      const contactGroup =  this.contact.groups.data.find(group => group.id === id ) || null;
      if(contactGroup) {
        return contactGroup.tra_id
      } else if (contactGroup == null) {
        let group = this.prepareMessages.find(message => message.group_id === id )
        return group.recipient_name.tra_id
      } else {
        return null
      }

    },
    toggleDeletionModal(row) {
      if (!this.showDeletionModal) {
        this.contactSetForDeletion = row;
      }
      this.showDeletionModal = !this.showDeletionModal;
      document.body.style.overflow = null
    },
    getFormattedColumnName(columnName) {
      return columnName.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    },
    getFormatedValue(value, columnName){
      if(columnName == 'phone_number'){
        var cleaned = ('' + value).replace(/\D/g, '');
        var match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
        if (match) {
          // var intlCode = (match[1] ? '+1 ' : '');
          return [match[1], ' (', match[2], ') ', match[3], '-', match[4]].join('');
        }
        return null;
      } else {
        return value;
      }
    }
  }
};
</script>
<style scoped lang="scss">
span.label {
  text-transform: capitalize;
  cursor:help;
  &.delivered {
    @apply bg-green-300;
    @apply text-green-800;
  }
  &.received {
    @apply bg-blue-300;
    @apply text-blue-800;
  }
  &.pending {
    @apply bg-yellow-300;
    @apply text-yellow-800;
  }
  &.undelivered, &.error, &.failed, &.attempt_rejected {
    @apply bg-red-300;
    @apply text-red-800;
  }
}
</style>
